import React, { useState, useEffect, Fragment } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { Header, Footer, Toc, Viewer, Navbar, Designer, Description, Dashboard } from '../../components';
import { Fullscreen, History } from '../../icons';
import { DataEntity } from '../../types/data';
import { Routes } from '../../enums';
import { getExpandedNodes, getFullPath, getDataEntity } from '../../utils';
import styles from './reports.module.css';

type LayoutProps = {
  data: DataEntity;
  entity: DataEntity;
  isDashboard: boolean;
  expandedNodes: string[];
  onToggleNode: (nodeId: string) => void;
};

const publicUrl = process.env.PUBLIC_URL || '';

function Layout({ data, entity, isDashboard, expandedNodes, onToggleNode }: LayoutProps) {
  const [isDesignMode, setDesignMode] = useState<boolean>(false);
  const [isFullScreenMode, setFullScreenMode] = useState<boolean>(false);
  const [designerPreview, setDesignerPreview] = useState<string | null>(null);
  const [theme, setTheme] = useState<string | null>(null);
  const [isTocOpen, toggleToc] = useState<boolean>(true);

  useEffect(() => {
    setDesignMode(false);
    setDesignerPreview(null);
  }, [entity, setDesignerPreview, setDesignMode]);

  // helpers
  const isReport = !!entity.report;
  const toc = data && data.children ? data.children : [];

  const renderTocPanel = () => (
    <div className={styles.toc}>
      {!!toc.length && <Toc data={toc} expandedNodes={expandedNodes} onToggle={onToggleNode} />}
    </div>
  );

  const renderTabs = () => (
    <div className={styles.tabs}>
      <div className={styles.elastic}>
        {isFullScreenMode && (
          <div className={styles.fsTab} onClick={() => setFullScreenMode(!isFullScreenMode)}>
            <Fullscreen />
            <span>Exit Full Screen</span>
          </div>
        )}
      </div>
    </div>
  );

  const renderPlayground = () => (
    <div
      className={`${styles.playground} ${isReport ? '' : styles.playground_hidden} ${
        isFullScreenMode ? styles.playground_fs : ''
      }`}
    >
      {isFullScreenMode && renderTabs()}
      {isDesignMode ? (
        <Designer onPreview={handlePreview} report={report} isDashboard={entity.isDashboard} />
      ) : (
        <Viewer
          report={report}
          theme={theme}
          undoable={!!designerPreview}
          editable={entity.designer}
          fitToWidth={entity.fitToWidth}
          hideToolbar={entity.hideToolbar}
          hideSidebar={entity.hideSidebar}
          chartAnimation={entity.chartAnimation}
          tableAnimation={entity.tableAnimation}
          onDesign={() => setDesignMode(true)}
          onUndo={() => setDesignerPreview(null)}
        />
      )}
    </div>
  );

  const handlePreview = (reportId: string, theme: any) => {
    setDesignerPreview(reportId);
    setTheme(theme);
    setDesignMode(false);
  };

  const report = designerPreview ? designerPreview : entity.report;

  return (
    <div className={`${styles.root} ${isFullScreenMode ? styles.root_fs : ''}`}>
      <div className={`${styles.head} ${isFullScreenMode ? styles.head_fs : ''}`}>
        <Header />
        <Navbar data={data} isTocOpen={isTocOpen} onToggleToc={() => toggleToc(!isTocOpen)} />
      </div>
      <div className={styles.body}>
        {isTocOpen && renderTocPanel()}
        <div className={styles.content}>
          <div className={styles.description}>
            <div className={styles.primary}>
              <div className={styles.title}>
                <h1>{entity.title}</h1>
              </div>
            </div>
            <div className={styles.secondary}>
              {isDashboard ? (
                <div className={styles.dashboard}>
                  {entity.description?.split('  ').map((t, i) => (
                    <p key={`p${i}`}>{t}</p>
                  ))}
                </div>
              ) : (
                <Description description={entity.description} childrenReports={isDashboard ? [] : entity.children} />
              )}
            </div>
            {isReport && renderTabs()}
          </div>
          {isDashboard ? <Dashboard /> : renderPlayground()}
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default function Reports() {
  const { pathname } = useLocation();
  const history = useHistory();
  const [data, setData] = useState<DataEntity>();
  const [isDashboard, setIsDashboard] = useState(true);
  const [expandedNodes, setExpandedNodes] = useState<string[]>([]);

  // Handle DidMount
  useEffect(() => {
    if (!expandedNodes.length) {
      fetch(`${publicUrl}/assets/data.json`)
        .then((response) => response.json())
        .then((tree) => {
          if (!tree) throw new Error(`Exception: Can't parse "data.json" file`);
          setData(tree);
          if (isDashboard) {
            setExpandedNodes([]);
          } else {
            const nodes = getExpandedNodes(tree, pathname);
            setExpandedNodes(nodes);

            if(nodes.length == 0)
              history.push(Routes.PageNotFound);
          }
        })
        .catch((error) => {
          throw new Error(`Exception: Can't read "data.json" file: ${error}`);
        });
    }
  }, [expandedNodes.length, pathname, isDashboard]);

  useEffect(() => {
    setIsDashboard(pathname === Routes.Reports);
  }, [pathname]);

  if (!data) return null;

  // handler of toggle the navigation menu nodes.
  const toggleNode = (nodeId: string): void => {
    const isExpanded = expandedNodes.includes(nodeId);
    let nodes: string[] = [];
    if (isExpanded) {
      nodes = expandedNodes.filter((element) => element !== nodeId);
    } else {
      nodes = [...expandedNodes, nodeId];
    }
    setExpandedNodes(nodes);
  };

  // collect data about a document.
  let entity = undefined;

  if (isDashboard) {
    entity = data;
  } else {
    const entityPath = getFullPath(data, pathname);
    entity = getDataEntity(data, entityPath);
  }

  if (!entity) return null;

  document.title = `${entity.seoTitle || entity.title} | .NET Reporting Demos | ActiveReports`;

  return (
    <Fragment>
      <Layout
        entity={entity}
        data={data}
        expandedNodes={expandedNodes}
        onToggleNode={toggleNode}
        isDashboard={isDashboard}
      />
    </Fragment>
  );
}
